import { formatDuration } from './formatters'
import {
  type Assessment,
  type AssessmentField,
  type AssessmentFieldValue,
  type AssessmentFieldResult,
  type CriticalAction,
  type CriticalActionDetailSection,
  type CriticalActionEvent,
  type CriticalActionResult,
  type CriticalSubActionEvent,
  type FieldStatus,
  type FieldValue,
  type Flow,
  type PlayerHintEvent,
  type PlayerRun,
  type ResultStatus,
  type RuleResult,
  type RunResult,
  type ScoringResult,
  type ScoringCriticalAction,
  type ScoringRubric,
  type ScoringRule,
  type Stage,
  type StageResult,
  type SubActionResult,
  type SupplyQuantity,
  type SupplySelectionItem,
  type SupplySelectionResult,
  type TechniqueRationales
} from './model'

export function moduleStages(
  module: { flow?: Flow; rubric?: ScoringRubric; stages?: Stage[] },
  techniqueRationales?: TechniqueRationales
): Stage[] | undefined {
  return toStages(module.flow, module.rubric) ?? withRationales(module.stages, techniqueRationales)
}

function withRationales(stages: Stage[] | undefined, techniqueRationales: TechniqueRationales | undefined) {
  const rationales = techniqueRationales?.rationales ?? {}
  return stages?.map(stage => ({
    ...stage,
    criticalActions: stage.criticalActions.map(x => ({ ...x, rationale: rationales[x.id] }))
  }))
}

export function toStages(flow: Flow | undefined, rubric: ScoringRubric | undefined): Stage[] | undefined {
  if (!flow) {
    return undefined
  }
  const criticalActions = rubric?.criticalActions ?? []
  return flow.stages.map(stage => ({
    id: stage.id,
    name: stage.name,
    criticalActions: criticalActions
      .filter(criticalAction => criticalAction.stageId === stage.id)
      .map(criticalAction => ({
        id: criticalAction.id,
        description: criticalAction.displayName,
        rationale: criticalAction.rationale,
        rules: criticalAction.rules,
        children: criticalAction.children,
        criticalSubActions: criticalAction.children?.map(x => ({ id: x.id, description: x.displayName })),
        domainsOfExcellence: (criticalAction.domainsOfExcellence ?? []).filter(domain => domain !== 'None')
      }))
  }))
}

export function toRunResult(playerRun: PlayerRun, stages: Stage[], assessments: Assessment[] = []): RunResult {
  const criticalActionsById = new Map(playerRun.criticalActions.map(x => [x.id, x]))
  const scoredRun: ScoredRun = {
    criticalActionsById,
    resultsByCriticalActionId: new Map((playerRun.criticalActionResults ?? []).map(x => [x.id, x.result])),
    ruleResultsByCriticalActionId: new Map(
      (playerRun.criticalActionResults ?? []).map(x => [x.id, x.ruleResults ?? []])
    ),
    assessments
  }
  const supplySelectionsByCriticalActionId = new Map(playerRun.supplySelections.map(x => [x.id, x]))
  const criticalSubActionsById = new Map(playerRun.criticalSubActions.map(x => [`${x.parent}:${x.id}`, x]))
  const hintsByCriticalAction = new Map<string, PlayerHintEvent[]>()
  for (const hint of playerRun.hints) {
    if (hint.criticalAction) {
      const hints = hintsByCriticalAction.get(hint.criticalAction) ?? []
      hints.push(hint)
      hintsByCriticalAction.set(hint.criticalAction, hints)
    }
  }

  let totalCriticalActions = 0
  let passedCriticalActions = 0

  const stageResults: StageResult[] = stages.map(stage => {
    let allPassed = true
    let stageHints: PlayerHintEvent[] = []

    const criticalActionResults: CriticalActionResult[] = stage.criticalActions.map(criticalAction => {
      const completed = criticalActionsById.get(criticalAction.id)
      const passed = completed?.passed ?? false
      if (passed) {
        passedCriticalActions++
      } else {
        allPassed = false
      }

      const criticalActionHints = hintsByCriticalAction.get(criticalAction.id) ?? []
      stageHints = [...stageHints, ...criticalActionHints]

      const subActionResults = criticalAction.children
        ? rubricChildResults(criticalAction.children, scoredRun)
        : moduleBuildSubActionResults(criticalAction, criticalSubActionsById)
      const criticalActionDetails = getCriticalActionDetails(
        criticalAction,
        supplySelectionsByCriticalActionId.get(criticalAction.id)?.itemsPlaced ?? [],
        criticalSubActionsById,
        subActionResults
      )

      return {
        passed,
        criticalAction,
        hints: criticalActionHints,
        criticalActionDetails,
        ...toAssessmentResult(criticalAction, passed, scoredRun)
      }
    })

    totalCriticalActions += criticalActionResults.length

    return {
      title: stage.name,
      criticalActionResults,
      hints: stageHints,
      passed: allPassed
    }
  })

  return {
    id: playerRun.id,
    score: playerRun.score,
    baseScore: playerRun.baseScore,
    passed: playerRun.passed,
    isTest: playerRun.isTest,
    completed: playerRun.completed,
    time: playerRun.time,
    hintsUsed: playerRun.hintsUsed,
    failedContaminationCriticalActionCount: playerRun.failedContaminationCriticalActionCount,
    failedBladderScanCriticalActionCount: playerRun.failedBladderScanCriticalActionCount,
    courseId: playerRun.courseId,
    multiplayerSessionId: playerRun.multiplayerSessionId,
    exitModule: playerRun.exitModule,
    stageResults,
    passedCriticalActions,
    totalCriticalActions,
    formattedDuration: formatDuration(playerRun.time),
    techniqueId: playerRun.techniqueId
  }
}

type ScoredRun = {
  criticalActionsById: Map<string, CriticalActionEvent>
  resultsByCriticalActionId: Map<string, ScoringResult>
  ruleResultsByCriticalActionId: Map<string, RuleResult[]>
  assessments: Assessment[]
}

function rubricChildResults(children: ScoringCriticalAction[], scoredRun: ScoredRun): SubActionResult[] {
  return children.map(child => {
    const status = toScoredStatus(child.id, scoredRun)
    return {
      id: child.id,
      status,
      description: child.displayName,
      ...toAssessmentResult(child, status === 'correct', scoredRun)
    }
  })
}

function moduleBuildSubActionResults(
  criticalAction: CriticalAction,
  criticalSubActionsById: Map<string, CriticalSubActionEvent>
): SubActionResult[] {
  return (criticalAction.criticalSubActions ?? [])
    .filter(x => !x.supplySelectionItems?.length)
    .map(x => ({
      id: x.id,
      status: toResultStatus(criticalSubActionsById.get(`${criticalAction.id}:${x.id}`)),
      description: x.description
    }))
}

function getCriticalActionDetails(
  criticalAction: CriticalAction,
  itemsPlaced: string[],
  criticalSubActionsById: Map<string, CriticalSubActionEvent>,
  subActionResults: SubActionResult[]
): CriticalActionDetailSection[] | undefined {
  const subActions = criticalAction.criticalSubActions ?? []
  const completionStatus = (subActionId: string) =>
    toResultStatus(criticalSubActionsById.get(`${criticalAction.id}:${subActionId}`))

  const supplySubActions = subActions
    .map(subAction => ({ subAction, items: subAction.supplySelectionItems ?? [] }))
    .filter(x => x.items.length)

  const requiredSelections: SupplySelectionResult[] = supplySubActions
    .filter(x => x.items.some(item => item.correct))
    .map(({ subAction, items }) => {
      const placed = placedItems(items, itemsPlaced)
      return {
        id: subAction.id,
        status: placed.length ? completionStatus(subAction.id) : 'incomplete',
        expected: toSupplyQuantities(items.filter(item => item.correct)),
        placed: toSupplyQuantities(placed)
      }
    })

  const extraSelections: SupplySelectionResult[] = supplySubActions
    .filter(x => !x.items.some(item => item.correct))
    .flatMap(({ subAction, items }) =>
      placedItems(items, itemsPlaced).map(
        (item): SupplySelectionResult => ({
          id: `${subAction.id}-${item.id}`,
          status: 'extra',
          expected: [],
          placed: [{ name: item.name, count: 1 }]
        })
      )
    )

  const sections: CriticalActionDetailSection[] = []
  if (requiredSelections.length) {
    sections.push({ kind: 'supplies', items: requiredSelections })
  }
  if (extraSelections.length) {
    sections.push({ kind: 'extraSupplies', items: extraSelections })
  }
  if (subActionResults.length) {
    sections.push({ kind: 'subActions', items: subActionResults })
  }
  return sections.length ? sections : undefined
}

function toAssessmentResult(
  criticalAction: { id: string; rules?: ScoringRule[] },
  passed: boolean,
  scoredRun: ScoredRun
): Pick<CriticalActionResult, 'assessment' | 'assessmentField'> {
  const assessment = scoredRun.assessments.find(x => x.criticalActionIds.includes(criticalAction.id))
  if (!assessment) {
    return {}
  }
  const ruleResultsByRuleId = new Map(
    (scoredRun.ruleResultsByCriticalActionId.get(criticalAction.id) ?? []).map(x => [x.ruleId, x])
  )
  const flattenedRules = (criticalAction.rules ?? []).flatMap(flattenRules)
  const gradingByFieldId = new Map(
    flattenedRules
      .flatMap(x => fieldRuleOf(x) ?? [])
      .map(x => [x.fieldId, { expected: x.expected, ruleResult: ruleResultsByRuleId.get(x.ruleId) }])
  )
  const rowSavedRule = flattenedRules.find(x => x.activityCompleted)
  const rowSaved = !!rowSavedRule && ruleResultsByRuleId.get(rowSavedRule.id!)?.outcome === 'Passed'
  const tabs = assessment.tabs
    .map(tab => ({
      id: tab.id,
      label: tab.label,
      fields: tab.fields.flatMap(field => {
        const grading = gradingByFieldId.get(field.id)
        return grading ? [toAssessmentFieldResult(field, grading, rowSaved, passed)] : []
      })
    }))
    .filter(x => x.fields.length)
  const [firstGradedField, ...otherGradedFields] = tabs.flatMap(x => x.fields)
  if (!firstGradedField) {
    return {}
  }
  if (!otherGradedFields.length) {
    return { assessmentField: firstGradedField }
  }
  return { assessment: { label: assessment.label, tabs } }
}

export function correctCount(fields: AssessmentFieldResult[]) {
  return fields.filter(x => x.status === 'correct').length
}

export function needsReview(field: AssessmentFieldResult | undefined): field is AssessmentFieldResult {
  return !!field && field.status !== 'correct'
}

type EnteredValue = { optionIds: string[] } | { value: number }
type FieldRule = { ruleId: string; fieldId: string; expected: EnteredValue }
type FieldGrading = { expected: EnteredValue; ruleResult?: RuleResult }

function toAssessmentFieldResult(
  field: AssessmentField,
  grading: FieldGrading,
  rowSaved: boolean,
  passed: boolean
): AssessmentFieldResult {
  const status = passed ? 'correct' : fieldStatus(grading, rowSaved)
  const charted = enteredValueOf(grading.ruleResult?.actual)
  return {
    id: field.id,
    label: field.label,
    status,
    charted: charted && toFieldValue(field, charted),
    expected: toFieldValue(field, grading.expected)
  }
}

function enteredValueOf(actual?: AssessmentFieldValue): EnteredValue | undefined {
  if (actual?.optionIds) {
    return { optionIds: actual.optionIds }
  }
  return actual?.value === undefined ? undefined : { value: actual.value }
}

function fieldStatus(grading: FieldGrading, rowSaved: boolean): FieldStatus {
  if (!rowSaved) {
    return 'notCharted'
  }
  const outcome = grading.ruleResult?.outcome
  return outcome ? fieldStatusByOutcome[outcome] : 'notCharted'
}

function toFieldValue(field: AssessmentField, value: EnteredValue): FieldValue {
  return 'optionIds' in value
    ? { optionLabels: optionLabels(field, value.optionIds) }
    : { value: value.value, unit: field.unit }
}

function optionLabels(field: AssessmentField, optionIds: string[]): string[] {
  return (field.options ?? []).filter(x => optionIds.includes(x.id)).map(x => x.label)
}

function flattenRules(rule: ScoringRule): ScoringRule[] {
  if (rule.rules) {
    return rule.rules.flatMap(flattenRules)
  }
  if (rule.not) {
    return flattenRules(rule.not)
  }
  return [rule]
}

function fieldRuleOf(rule: ScoringRule): FieldRule | undefined {
  if (rule.optionSelected) {
    return {
      ruleId: rule.id!,
      fieldId: rule.optionSelected.fieldId,
      expected: { optionIds: rule.optionSelected.optionIds }
    }
  }
  if (rule.valueEntered) {
    return { ruleId: rule.id!, fieldId: rule.valueEntered.fieldId, expected: { value: rule.valueEntered.value } }
  }
  return undefined
}

const fieldStatusByOutcome: Record<ScoringResult, FieldStatus> = {
  Passed: 'correct',
  Failed: 'incorrect',
  NotAttempted: 'notCharted'
}

function toResultStatus(completed: { passed: boolean } | undefined): ResultStatus {
  return completed ? (completed.passed ? 'correct' : 'incorrect') : 'incomplete'
}

function toScoredStatus(criticalActionId: string, scoredRun: ScoredRun): ResultStatus {
  const result = scoredRun.resultsByCriticalActionId.get(criticalActionId)
  return result
    ? resultStatusByScoringResult[result]
    : toResultStatus(scoredRun.criticalActionsById.get(criticalActionId))
}

const resultStatusByScoringResult: Record<ScoringResult, ResultStatus> = {
  Passed: 'correct',
  Failed: 'incorrect',
  NotAttempted: 'incomplete'
}

function placedItems(items: SupplySelectionItem[], itemsPlaced: string[]): SupplySelectionItem[] {
  return itemsPlaced.map(itemId => items.find(item => item.id === itemId)).filter(item => !!item)
}

function toSupplyQuantities(items: SupplySelectionItem[]): SupplyQuantity[] {
  const names = [...new Set(items.map(x => x.name))]
  return names.map(name => ({ name, count: items.filter(x => x.name === name).length }))
}
